/**
 * FEAT-31 code replay — the browser side: one recorder per code question of a
 * REAL attempt, and the one upload queue they share.
 *
 * The recorder is fed the editor's full text on every change (the same string
 * that becomes `final_code`) and samples it at most every `sampleS` seconds:
 * one splice per sample (`diffSplice`), whole-second times, plus a marker for
 * a paste, a Run, a Reset or a restore. It never sees a key event and stores no per-key
 * timing. Steps are cut into chunks every `flushS` seconds and uploaded to
 * `POST /api/t/{token}/replay/{position}`.
 *
 * ⚠ NEVER in Practice or the recruiter's Preview — both say "not recorded".
 * Only `routes/Question.tsx` creates a recorder
 * (`codeReplayRecorder.test.ts` pins that).
 *
 * ⚠ A replay must never cost the candidate anything: no upload error is ever
 * shown, nothing here throws into the page, and the barrier callers await
 * before /submit (`flushReplayUploads`) always resolves, within a bound.
 *
 * Lost chunks: a chunk that cannot be uploaded is dropped and the NEXT chunk
 * is a keyframe flagged `gap`, so the player shows a break instead of
 * applying splices to the wrong text.
 */

import { diffSplice, type ReplayChunk, type ReplayRunResult, type ReplayStep } from "@/lib/codeReplay";

export type ReplayUploadResult = "ok" | "retry" | "drop" | "stop";

export type ReplayUploader = (
  token: string,
  attemptId: string,
  position: number,
  body: string,
) => Promise<ReplayUploadResult>;

// Chromium's keepalive budget is 64 KiB in flight; a larger body sent with
// `keepalive` is rejected outright (the `uploadSnapshot` rule).
const KEEPALIVE_MAX_CHARS = 60_000;
// The server refuses a chunk over 160 KiB; a body that cannot fit is never sent.
const MAX_BODY_BYTES = 150_000;
// A chunk is cut early at this many steps (the server's cap) or this much text.
const MAX_STEPS = 64;
const MAX_PENDING_CHARS = 40_000;
// A keyframe this large travels alone, ahead of the steps it anchors.
const SOLO_KEYFRAME_CHARS = 60_000;
const KEYFRAME_EVERY = 10;
const RETRY_DELAYS_MS = [1_000, 4_000, 10_000];
const MAX_QUEUED = 5;
// A paste event that no change follows within this long marks nothing.
const PASTE_ARM_MS = 1_000;
// Monaco reports a paste (`onDidPaste`) in the same tick as the change that
// applied it; a report this long after the last change had no change of its own.
const PASTE_SAME_CHANGE_MS = 50;

export async function uploadReplayChunk(
  token: string,
  attemptId: string,
  position: number,
  body: string,
): Promise<ReplayUploadResult> {
  let res: Response;
  try {
    res = await fetch(`/api/t/${token}/replay/${position}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Attempt-Id": attemptId },
      body,
      keepalive: body.length <= KEEPALIVE_MAX_CHARS,
    });
  } catch {
    return "retry";
  }
  if (res.ok) return "ok";
  // 404 = no replay for this attempt (or the platform switched off), 410 =
  // the attempt is over or the replay was purged: nothing more will be taken.
  if (res.status === 404 || res.status === 410) return "stop";
  if (res.status === 429) {
    // The per-attempt cap ends the recording; the rate limit is a wait.
    try {
      const data: unknown = await res.json();
      const code = (data as { detail?: { code?: string } })?.detail?.code;
      if (code === "REPLAY_CAP_REACHED") return "stop";
    } catch {
      // Not JSON: treat as the rate limit.
    }
    return "retry";
  }
  if (res.status >= 500) return "retry";
  // 409 (paused), 413 / 422 (a body no retry can fix): this chunk is lost.
  return "drop";
}

interface QueuedChunk {
  owner: RecorderState;
  body: string;
}

interface RecorderState {
  token: string;
  attemptId: string;
  position: number;
  upload: ReplayUploader;
  stopped: boolean;
  /** The next chunk must be a keyframe flagged `gap`. */
  gap: boolean;
}

const queue: QueuedChunk[] = [];
let draining: Promise<void> | null = null;
// The drain a barrier already gave up waiting for. The time-up path runs two
// barriers back to back (Question's transition, then Done's /submit): waiting
// again on the same stuck upload would double the candidate's wait.
let abandonedDrain: Promise<void> | null = null;
const liveRecorders = new Set<ReplayRecorder>();

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => window.setTimeout(resolve, ms));

async function sendWithRetries(item: QueuedChunk): Promise<void> {
  for (let attempt = 0; ; attempt += 1) {
    if (item.owner.stopped) return;
    let result: ReplayUploadResult;
    try {
      result = await item.owner.upload(
        item.owner.token,
        item.owner.attemptId,
        item.owner.position,
        item.body,
      );
    } catch {
      result = "retry";
    }
    if (result === "ok") return;
    if (result === "stop") {
      item.owner.stopped = true;
      return;
    }
    if (result === "drop" || attempt >= RETRY_DELAYS_MS.length) {
      item.owner.gap = true;
      return;
    }
    await wait(RETRY_DELAYS_MS[attempt]);
  }
}

async function drain(): Promise<void> {
  while (queue.length > 0) {
    const item = queue.shift() as QueuedChunk;
    await sendWithRetries(item);
  }
}

function kick(): void {
  if (draining !== null || queue.length === 0) return;
  draining = drain().finally(() => {
    draining = null;
    // A chunk queued between the loop's last check and this callback.
    kick();
  });
}

function enqueue(item: QueuedChunk): void {
  if (item.owner.stopped) return;
  if (queue.length >= MAX_QUEUED) {
    // The network has been down for minutes. Dropping the OLDEST keeps memory
    // bounded; its owner's next chunk becomes a keyframe.
    const dropped = queue.shift() as QueuedChunk;
    dropped.owner.gap = true;
  }
  queue.push(item);
  kick();
}

/**
 * The barrier before /submit, /pause and a section change: cut what every
 * live recorder is holding, then wait for the uploads — but never longer than
 * `maxWaitMs`, and never with a rejection. A replay is not worth one second
 * of a candidate's deadline.
 */
export async function flushReplayUploads(maxWaitMs = 1_500): Promise<void> {
  try {
    for (const recorder of liveRecorders) recorder.flush();
    const pending = draining;
    if (pending === null || pending === abandonedDrain) return;
    let timedOut = false;
    await Promise.race([
      pending.catch(() => undefined),
      wait(maxWaitMs).then(() => {
        timedOut = true;
      }),
    ]);
    if (timedOut) abandonedDrain = pending;
  } catch {
    // Never surfaces.
  }
}

export interface ReplayRecorderOptions {
  token: string;
  attemptId: string;
  position: number;
  /** The editor's text when recording starts (what the editor mounts with). */
  initialText: string;
  sampleS: number;
  flushS: number;
  /** Injectable for tests. */
  upload?: ReplayUploader;
  now?: () => number;
}

export interface ReplayRecorder {
  /** The editor's full text, on every change. */
  setText(text: string): void;
  /** A drop is about to land in the editor (the capture-phase DOM event). */
  beforePaste(): void;
  /** The editor reports a paste it has JUST applied (Monaco's `onDidPaste`,
   *  in the same tick as the change) — Ctrl+V and its menu Paste alike. */
  notePaste(): void;
  /** A Run finished, or Reset replaced the code. */
  mark(kind: "run" | "reset", run?: ReplayRunResult): void;
  /** B5: the editor mounted with `text`, the code kept in the tab across a
   *  reload (`lib/answerDraft.ts`), instead of the server's copy the
   *  recorder started from. One MARKED step — never typing. */
  restore(text: string): void;
  /** Sample now and queue what is pending. */
  flush(): void;
  /** Stop: a last flush, timers and listeners removed. Uploads carry on. */
  dispose(): void;
}

function newSessionId(): string {
  const alphabet = "abcdefghijklmnopqrstuvwxyz0123456789";
  const bytes = new Uint8Array(10);
  crypto.getRandomValues(bytes);
  let out = "";
  for (const byte of bytes) out += alphabet[byte % alphabet.length];
  return out;
}

export function createReplayRecorder(options: ReplayRecorderOptions): ReplayRecorder {
  const now = options.now ?? (() => Date.now());
  const state: RecorderState = {
    token: options.token,
    attemptId: options.attemptId,
    position: options.position,
    upload: options.upload ?? uploadReplayChunk,
    stopped: false,
    gap: false,
  };
  const sid = newSessionId();
  const sampleMs = Math.max(1, options.sampleS) * 1000;
  const flushMs = Math.max(options.sampleS, options.flushS) * 1000;

  let current = options.initialText;
  // The text before the most recent change, and when that change came — so a
  // paste reported AFTER it was applied can be told apart from the typing.
  let previous = options.initialText;
  let lastChangeAt = 0;
  let lastSampled = options.initialText;
  let steps: ReplayStep[] = [];
  // The open chunk: the text before its first step, and when that step came.
  let chunkBase = options.initialText;
  let chunkT0 = 0;
  let chunkStartedAt = 0;
  let pendingChars = 0;
  let n = 0;
  let pasteArmedAt: number | null = null;
  let lastPasteStepAt = 0;
  let disposed = false;

  /** Open a chunk on the text as last sampled. ⚠ Call BEFORE `lastSampled`
   *  moves — the chunk anchors on the text its first step applies to. */
  const beginChunk = (): void => {
    if (steps.length > 0) return;
    chunkBase = lastSampled;
    chunkStartedAt = now();
    chunkT0 = Math.floor(chunkStartedAt / 1000);
    pendingChars = 0;
  };

  const pushStep = (step: Omit<ReplayStep, "dt">): void => {
    steps.push({ ...step, dt: Math.max(0, Math.floor(now() / 1000) - chunkT0) });
    pendingChars += step.i.length;
    if (steps.length >= MAX_STEPS || pendingChars >= MAX_PENDING_CHARS) cut();
  };

  /** Record the change from the last sample to `target`, if any. True when it did. */
  const sampleTo = (target: string, kind?: "paste" | "reset" | "restore"): boolean => {
    const splice = diffSplice(lastSampled, target);
    if (splice === null) return false;
    beginChunk();
    lastSampled = target;
    pushStep(kind ? { ...splice, k: kind } : splice);
    if (kind === "paste") lastPasteStepAt = now();
    return true;
  };
  /** Record the change since the last sample, if any. True when it did. */
  const sample = (kind?: "paste" | "reset"): boolean => sampleTo(current, kind);

  const pushMarker = (step: Pick<ReplayStep, "k" | "run">): void => {
    beginChunk();
    pushStep({ o: 0, d: 0, i: "", ...step });
  };

  const send = (chunk: ReplayChunk): void => {
    const body = JSON.stringify(chunk);
    if (new TextEncoder().encode(body).length > MAX_BODY_BYTES) {
      // Cannot fit under the server's cap: never sent, and the record says so.
      state.gap = true;
      return;
    }
    enqueue({ owner: state, body });
  };

  function cut(): void {
    if (steps.length === 0 || state.stopped) {
      steps = [];
      return;
    }
    const gap = state.gap;
    const keyframe = n === 0 || gap || n % KEYFRAME_EVERY === 0;
    state.gap = false;
    const cutSteps = steps;
    steps = [];
    if (keyframe && chunkBase.length + pendingChars > SOLO_KEYFRAME_CHARS) {
      // A large keyframe goes alone so the pair stays under the body cap.
      send({
        v: 1, sid, n, t0: chunkT0, replay_base: chunkBase,
        base_len: chunkBase.length, gap, replay_steps: [],
      });
      n += 1;
      send({
        v: 1, sid, n, t0: chunkT0, replay_base: null,
        base_len: chunkBase.length, gap: false, replay_steps: cutSteps,
      });
    } else {
      send({
        v: 1, sid, n, t0: chunkT0,
        replay_base: keyframe ? chunkBase : null,
        base_len: chunkBase.length, gap, replay_steps: cutSteps,
      });
    }
    n += 1;
  }

  const tick = (): void => {
    if (disposed || state.stopped) return;
    sample();
    if (steps.length > 0 && now() - chunkStartedAt >= flushMs) cut();
  };
  const timer = window.setInterval(tick, sampleMs);

  const flush = (): void => {
    if (state.stopped) return;
    sample();
    cut();
  };
  const onPageHide = (): void => flush();
  const onVisibility = (): void => {
    if (document.visibilityState === "hidden") flush();
  };
  window.addEventListener("pagehide", onPageHide);
  document.addEventListener("visibilitychange", onVisibility);

  const recorder: ReplayRecorder = {
    setText(text: string): void {
      if (disposed) return;
      previous = current;
      current = text;
      lastChangeAt = now();
      if (pasteArmedAt !== null) {
        const armedFor = now() - pasteArmedAt;
        pasteArmedAt = null;
        if (armedFor <= PASTE_ARM_MS) sample("paste");
      }
    },
    beforePaste(): void {
      if (disposed || state.stopped) return;
      // Close what was typed so far, so the pasted span is a step of its own.
      sample();
      pasteArmedAt = now();
    },
    notePaste(): void {
      if (disposed || state.stopped) return;
      // The DOM `drop` path already marked this one.
      if (now() - lastPasteStepAt <= PASTE_ARM_MS) return;
      pasteArmedAt = null;
      // A paste that changed nothing: the last change was typing, not this.
      if (now() - lastChangeAt > PASTE_SAME_CHANGE_MS) return;
      // The editor reports a paste AFTER applying it, so `current` already
      // holds it: what was typed before it goes first, as its own step, and
      // the paste step holds the pasted text alone.
      sampleTo(previous);
      sample("paste");
    },
    mark(kind: "run" | "reset", run?: ReplayRunResult): void {
      if (disposed || state.stopped) return;
      if (kind === "reset") {
        // A Reset that changed nothing (the code WAS the starter) still shows.
        if (!sample("reset")) pushMarker({ k: "reset" });
        return;
      }
      sample();
      pushMarker({ k: "run", ...(run ? { run } : {}) });
    },
    restore(text: string): void {
      if (disposed || state.stopped) return;
      previous = text;
      current = text;
      // Equal to where the recorder started: nothing was restored.
      sampleTo(text, "restore");
    },
    flush,
    dispose(): void {
      if (disposed) return;
      flush();
      disposed = true;
      window.clearInterval(timer);
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onVisibility);
      liveRecorders.delete(recorder);
    },
  };
  liveRecorders.add(recorder);
  return recorder;
}

/** Tests only: forget every queued chunk and live recorder. */
export function resetReplayQueueForTests(): void {
  queue.length = 0;
  draining = null;
  abandonedDrain = null;
  liveRecorders.clear();
}
